@if (state.loading) {
  <p role="status">Loading saved membership criteria…</p>
}
@if (state.error) {
  <p role="alert">{{ state.error }}</p>
}
@if (!teamId && !editorOnly) {
  <section class="selection-mode" aria-label="Choose employees for a new team">
    <h3>Create a team</h3>
    <mat-radio-group [(ngModel)]="allMatching" (change)="selectionChanged()" [disabled]="operationActive" aria-label="Employee selection method">
      <mat-radio-button [value]="false">Choose employees</mat-radio-button>
      <mat-radio-button [value]="true">Use membership criteria</mat-radio-button>
    </mat-radio-group>
  </section>
}
@if (editorOnly || (teamId ? state.saved : allMatching)) {
  <section class="criteria-panel" aria-label="Membership criteria">
    @if (!editorOnly) {
      <h3>{{ teamId ? 'Add employees using saved criteria' : 'Membership criteria' }}</h3>
    }
    @if (!editorOnly && teamId) {
      <p>Preview the saved criteria, then add newly matching employees. Current members stay on the team and previously removed members remain excluded.</p>
    }
    @if (!editorOnly && teamId && !state.saved?.criteria) {
      <p>No saved definition. Choose and save criteria deliberately before adding newly matching employees.</p>
    }
    <fieldset [disabled]="operationActive || state.busy" aria-label="Membership inclusion criteria">
      <mat-checkbox [ngModel]="state.criteria.allEligible" (ngModelChange)="state.setAllEligible($event)" [disabled]="operationActive || state.busy">All eligible employees</mat-checkbox>
      @if (!state.criteria.allEligible) {
        <div>
          <div class="criteria-controls">
            <mat-form-field appearance="outline"><mat-label>Include employees by</mat-label><mat-select [(ngModel)]="state.field" (selectionChange)="state.prefix = ''; state.loadOptions()" [disabled]="operationActive || state.busy">@for (field of state.fields; track field) {
            <mat-option [value]="field">{{ state.labels[field] }}</mat-option>
          }</mat-select></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Value starts with</mat-label><input matInput [(ngModel)]="state.prefix" maxlength="191" (ngModelChange)="state.loadOptions()" [disabled]="operationActive || state.busy"></mat-form-field>
          <button mat-stroked-button type="button" (click)="state.loadOptions()" [disabled]="state.optionsBusy || operationActive || state.busy">Find values</button>
          @if (state.options.length) {
            <mat-form-field appearance="outline"><mat-label>Include value</mat-label><mat-select [value]="null" [disabled]="state.optionsBusy || operationActive || state.busy" (selectionChange)="state.addOption($event.value)">@for (value of state.options; track value) {
            <mat-option [value]="value">{{ value.value }}</mat-option>
          }</mat-select></mat-form-field>
        }
        @if (state.optionsNext) {
          <button mat-button type="button" (click)="state.loadOptions(true)" [disabled]="state.optionsBusy || operationActive || state.busy">More values</button>
        }
        @if (state.optionsError) {
          <span role="alert">{{ state.optionsError }}</span>
        }
        @if (state.optionsLoaded && !state.options.length) {
          <span role="status">No values found for this field and prefix.</span>
        }
        @if (state.optionsBusy) {
          <span role="status">Finding values…</span>
        }
      </div>
      <mat-chip-set aria-label="Selected membership criteria">
        @for (rule of state.criteria.rules; track rule) {
          @for (value of rule.values; track value) {
            <mat-chip [removable]="!operationActive && !state.busy" (removed)="state.remove(rule.field, value)">
              {{ state.labels[rule.field] }}: {{ value }}@if (!operationActive && !state.busy) {
              <button matChipRemove [attr.aria-label]="'Remove ' + state.labels[rule.field] + ': ' + value"><mat-icon>cancel</mat-icon></button>
            }
          </mat-chip>
        }
      }
    </mat-chip-set>
    <p>Values within a field are alternatives; all selected fields must match.</p>
  </div>
}
@if (!editorOnly && teamId && state.dirty) {
  <p role="status">Unsaved criteria. Saving changes no memberships; save before previewing additions.</p>
}
@if (!editorOnly && teamId) {
  <button mat-stroked-button type="button" (click)="state.save()" [disabled]="!state.valid || !state.dirty || operationActive || state.busy">Save criteria</button>
}
@if (!editorOnly) {
  <button mat-stroked-button type="button" (click)="state.loadPreview()" [disabled]="!state.valid || (teamId && state.dirty) || operationActive || state.busy">Preview matching employees</button>
}
</fieldset>
@if (state.busy && !editorOnly) {
  <p role="status">Checking matching employees…</p>
}
@if (!editorOnly && state.preview; as preview) {
  <p role="status">{{ preview.complete ? 'Preview complete' : 'Preview in progress — counts are partial' }}: {{ preview.counts.scannedRows }} checked · {{ preview.counts.matchingRows }} match criteria · {{ preview.counts.eligibleRows }} eligible · {{ preview.counts.blockedRows }} missing required onboarding evidence.</p>
  <p>Collected {{ preview.collectedAt | date:'medium' }}. Account matches, current members, removals and proposed additions are checked before any account preparation.</p>
  @if (preview.employees.length) {
    <details><summary>View matching employees ({{ preview.employees.length }} shown)</summary><ul>@for (employee of preview.employees; track employee) {
    <li>{{ employee.firstName }} {{ employee.lastName }} · {{ employee.department || 'No department' }} · {{ employee.jobTitle || 'No payroll job title' }}</li>
  }</ul></details>
}
@if (preview.counts.matchingRows > preview.employees.length) {
  <p>Preview shows up to 50 matching rows.</p>
}
}
@if (!editorOnly && !teamId && allMatching) {
  <p>Complete the preview, then choose Create team. These criteria will be saved with the team. Up to 100 entries may be prepared; the server checks the final count before onboarding.</p>
}
@if (state.preview?.complete && !state.preview?.counts?.eligibleRows) {
  <p role="status">No eligible employees match these criteria.</p>
}
@if (!editorOnly && teamId && state.preview?.complete) {
  <p>Eligible matches include current members. The operation checks account matches and existing memberships before calculating actual additions.</p>
}
@if (!editorOnly && teamId) {
  <button mat-raised-button color="primary" type="button" (click)="refresh()" [disabled]="!state.selection || operationActive">Add newly matching employees</button>
}
@if (!editorOnly && teamId) {
  <p>At most 100 employee entries may require addition or preparation per operation. Existing active memberships do not use this allowance.</p>
}
@if (!editorOnly && teamId && creation) {
  <section aria-label="Membership operation progress">
    @if (creation.error) {
      <p role="alert">{{ creation.error }}</p>
    }
    @if (creation.progress; as progress) {
      <p role="status"><strong>{{ creation.progressMessage }}</strong></p>
      @if (progress.phase === 'preparing' || progress.phase === 'needs_review') {
        <p>{{ progress.preparedCount }} of {{ progress.selectedCount }} employee entries prepared. Membership has not committed.</p>
      }
      @if (progress.phase === 'team_committed' || progress.phase === 'complete') {
        <p>{{ progress.memberCount }} {{ progress.purpose === 'add_members' ? 'employees added' : 'team members' }}.</p>
      }
      @if (progress.preflight; as counts) {
        <p>{{ counts.complete ? 'Checks complete' : 'Checking employees — counts are partial' }} · {{ counts.matchingRows }} match criteria · {{ counts.eligibleRows }} eligible · {{ counts.proposedEntries }} proposed for preparation / addition</p>
        <details><summary>View account and membership checks</summary><p>{{ counts.resolvedDistinctUsers }} distinct existing accounts · {{ counts.currentMemberRows }} current member rows · {{ counts.removedMemberRows }} previously removed rows · {{ counts.blockedRows }} blocked. Matching employee rows are not a count of actual additions.</p></details>
      }
      @for (issue of progress.issues; track issue) {
        <p role="alert">{{ issue.split('_').join(' ') }}. Review before resuming.</p>
      }
      @if (!creation.busy && progress.phase !== 'complete' && progress.phase !== 'closed') {
        <button mat-button (click)="creation.resume(progress.operationId)">Resume</button>
      }
      @if (!creation.busy && progress.phase !== 'complete' && progress.phase !== 'closed') {
        <button mat-button (click)="creation.close()">Close recovery (keeps prepared accounts and memberships)</button>
      }
    }
    @for (operation of creation.recoverable; track operation) {
      <div><span>Unfinished {{ operation.purpose === 'add_members' ? 'employee addition' : 'team creation' }}</span><button mat-button [disabled]="creation.busy" (click)="creation.resume(operation.operationId)">Resume</button></div>
    }
  </section>
}
</section>
}
